import { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import MealsContext from '../../context/meals-context';
import MealPlansContext from '../../context/meal-plans-context';
import { addMealToPlan as addMealToPlanApi } from '../../api/meal-plan-api';
import TagSelector from '../Meal/Tag/TagSelector';
import Meal from '../../models/meal';

interface SuggestionWizardProps {
  planId: string;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const steps = ['Počet jídel', 'Filtry', 'Vyloučit nedávná', 'Výsledky'];

const fisherYatesShuffle = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const SuggestionWizard = ({ planId, open, onClose, onComplete }: SuggestionWizardProps) => {
  const { meals } = useContext(MealsContext);
  const { refresh } = useContext(MealPlansContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [mealCount, setMealCount] = useState(5);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [excludeRecent, setExcludeRecent] = useState(true);
  const [excludeDays, setExcludeDays] = useState(7);
  const [proposedMeals, setProposedMeals] = useState<Meal[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setMealCount(5);
      setSelectedTagIds([]);
      setExcludeRecent(true);
      setExcludeDays(7);
      setProposedMeals([]);
      setWarning(null);
      setError(null);
    }
  }, [open]);

  const generateSuggestions = () => {
    let pool = [...meals];

    // Filter by tags
    if (selectedTagIds.length > 0) {
      pool = pool.filter((meal) => meal.tags.some((tag) => selectedTagIds.includes(tag.id)));
    }

    // Exclude recently cooked
    if (excludeRecent) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - excludeDays);
      pool = pool.filter((meal) => meal.lastCooking === null || meal.lastCooking < cutoff);
    }

    // Shuffle and take
    const shuffled = fisherYatesShuffle(pool);
    const selected = shuffled.slice(0, mealCount);

    if (selected.length < mealCount) {
      setWarning(`Nalezeno pouze ${selected.length} jídel z ${mealCount} požadovaných`);
    } else {
      setWarning(null);
    }

    setProposedMeals(selected);
  };

  const handleNext = () => {
    if (activeStep === 2) {
      generateSuggestions();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRemoveMeal = (mealId: string) => {
    setProposedMeals((prev) => prev.filter((m) => m.id !== mealId));
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const meal of proposedMeals) {
        await addMealToPlanApi(planId, meal.id);
      }
      await refresh();
      onComplete();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography>Kolik jídel chcete navrhnout?</Typography>
            <TextField
              type="number"
              value={mealCount}
              onChange={(e) => setMealCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: 20 }}
              size="small"
              sx={{ width: 120 }}
            />
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography>Filtrovat podle tagů (volitelné)</Typography>
            <TagSelector selectedTagIds={selectedTagIds} onChange={setSelectedTagIds} />
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={excludeRecent} onChange={(e) => setExcludeRecent(e.target.checked)} />}
              label="Vynechat nedávno uvařená jídla"
            />
            {excludeRecent && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Uvařená za posledních</Typography>
                <TextField
                  type="number"
                  value={excludeDays}
                  onChange={(e) => setExcludeDays(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  size="small"
                  sx={{ width: 80 }}
                />
                <Typography>dní</Typography>
              </Stack>
            )}
          </Stack>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            {warning && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {warning}
              </Alert>
            )}
            {proposedMeals.length === 0 ? (
              <Typography color="text.secondary">Žádná jídla neodpovídají vybraným kritériím</Typography>
            ) : (
              <List>
                {proposedMeals.map((meal) => (
                  <ListItem
                    key={meal.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleRemoveMeal(meal.id)} aria-label="Odebrat">
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={meal.name} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>Návrh jídel</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Zrušit
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={saving}>
            Zpět
          </Button>
        )}
        {activeStep < 3 ? (
          <Button onClick={handleNext} variant="contained">
            {activeStep === 2 ? 'Navrhnout' : 'Další'}
          </Button>
        ) : (
          <Button onClick={handleConfirm} variant="contained" disabled={saving || proposedMeals.length === 0}>
            {saving ? 'Přidávám...' : 'Potvrdit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuggestionWizard;
